export const PRODUCTION_STAGES = ["briefing", "scripting", "production", "editing", "review", "delivered"] as const;
export type ProductionStage = (typeof PRODUCTION_STAGES)[number];

export type UserRole = "client" | "account_manager" | "manager" | "admin" | "superadmin";

export type AccountSummary = { id: string; name: string };

export type SessionUser = {
  id: string;
  /** The client this session is currently working in. Staff can change it. */
  accountId: string;
  /** Where the user belongs. Never changes. */
  homeAccountId: string;
  email: string;
  displayName: string;
  role: UserRole;
  /** True while the account holds a password somebody else generated. */
  mustChangePassword: boolean;
  /** Email consent: true yes, false no, null never asked. Clients only. */
  emailOptIn: boolean | null;
};

export type Service = {
  id: string;
  /** Staff only — the server omits these for clients. */
  standardCredits?: number;
  hasCustomRate?: boolean;
  name: string;
  category: string;
  description: string;
  unit: string;
  /** The rate card in Arabic. Null on a row seeded before it existed — every
   *  reader goes through `serviceText()`, which falls back to the English. */
  nameAr: string | null;
  categoryAr: string | null;
  descriptionAr: string | null;
  unitAr: string | null;
  credits: number;
  rateKd: number | null;
  included: number;
};

export type RequestStatus = "pending" | "approved" | "in_progress" | "completed" | "rejected" | "canceled";

export type ServiceRequest = {
  id: string;
  service_id: string;
  service_name: string;
  service_category: string;
  title: string;
  brief: string;
  quantity: number;
  estimated_credits: number;
  final_credits: number | null;
  due_date: string | null;
  /** What Prismic committed to at approval. Distinct from the client's due_date. */
  promised_date: string | null;
  stage: ProductionStage | null;
  language: string;
  channel: string | null;
  source: string;
  reference_url: string | null;
  billing_month: string;
  status: RequestStatus;
  created_by_name: string;
  assigned_to_name: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  /** Cover pulled from the delivered work, null when nothing is delivered yet. */
  coverUrl?: string | null;
};

export type Attachment = {
  id: string;
  requestId: string | null;
  kind: "brief" | "deliverable";
  filename: string;
  mime: string;
  bytes: number;
  isCover: boolean;
  createdAt: string;
  uploadedByName: string | null;
  /** Which delivery batch this file belongs to. Null = delivered before versions
   *  existed; read as the only version, never as v0. */
  version: number | null;
  url: string;
};

export type Delivery = {
  id: string;
  url: string;
  filename: string;
  mime: string;
  bytes: number;
  requestId: string;
  requestTitle: string;
  /** So the gallery can name the service in the reader's language. */
  serviceId: string;
  serviceName: string;
  serviceCategory: string;
  credits: number;
  status: string;
  completedAt: string | null;
  deliveredBy: string | null;
};

export type CreditEntry = {
  id: string;
  month: string;
  kind: "allocation" | "rollover" | "topup" | "spend" | "adjustment" | "refund";
  credits: number;
  title: string;
  note: string | null;
  service_id: string | null;
  service_name: string | null;
  service_category: string | null;
  request_id: string | null;
  source: string;
  occurred_at: string;
  created_by_name: string | null;
};

export type DashboardData = {
  account: {
    id: string;
    name: string;
    monthlyCredits: number;
    creditValueKd: number;
    contractStart: string;
    contractEnd: string;
  };
  month: string;
  balance: {
    totalPool: number;
    spent: number;
    reserved: number;
    available: number;
    carriedIn: number;
    topups: number;
  };
  entries: CreditEntry[];
  requests: ServiceRequest[];
  categorySpend: Array<{ category: string; credits: number }>;
};

export type ActivityItem = {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  detail: string;
  user_name: string | null;
  created_at: string;
  /** The agent key the action came through ("Ajeeb AM"); null for the website. */
  via?: string | null;
};

export type AccountRecord = {
  id: string;
  name: string;
  monthly_credits: number;
  credit_value_kd: number;
  contract_start: string;
  contract_end: string;
};

export type NewClient = {
  id: string;
  name: string;
  monthlyCredits: number;
  creditValueKd: number;
  contractStart: string;
  contractEnd: string;
};

export type AdminUser = {
  id: string;
  email: string;
  display_name: string;
  role: UserRole;
  active: number;
  must_change_password: number;
  created_at: string;
  account_id: string;
  account_name: string;
  /** Clients this ACCOUNT MANAGER was granted, over and above their home account.
   *  Empty for every other role — a manager already reaches all of them and a
   *  client reaches only their own. See server/assignments.ts. */
  assignedAccountIds: string[];
};

export type NewUser = {
  accountId: string;
  email: string;
  displayName: string;
  role: UserRole;
};

export type PortfolioClient = {
  id: string;
  name: string;
  monthlyCredits: number;
  creditValueKd: number;
  contractStart: string;
  contractEnd: string;
  monthsElapsed: number;
  grantedToDate: number;
  spentToDate: number;
  unusedCredits: number;
  burnRate: number;
  annualValueKd: number;
  unusedValueKd: number;
  thisMonth: { totalPool: number; spent: number; reserved: number; available: number };
  openRequests: number;
  lastActivity: string | null;
};

export type RateOverride = {
  service_id: string;
  service_name: string;
  standard_credits: number;
  credits: number;
  note: string | null;
  updated_at: string;
};

export type TimelineEntry = {
  action: string;
  detail: string;
  at: string;
  actor: string | null;
};

export type FeedbackRating = "loved" | "good" | "changes";

export type Feedback = {
  rating: FeedbackRating;
  note: string | null;
  updated_at: string;
  user_id: string;
  author: string | null;
};

export type Handoff = {
  request: {
    id: string; title: string; brief: string; status: RequestStatus; quantity: number;
    language: string; channel: string | null; due_date: string | null;
    promised_date: string | null; stage: ProductionStage | null;
    created_at: string; completed_at: string | null; billing_month: string;
    credits: number; final_credits: number | null;
    service_id: string; service_name: string; service_category: string;
    requested_by: string | null; delivered_by: string | null;
  };
  files: Attachment[];
  feedback: Feedback[];
  mine: Feedback | null;
};

export type Insights = {
  month: string;
  previousMonth: string;
  thisMonth: number;
  lastMonth: number;
  /** null when there is no previous month to compare against — not the same as 0. */
  changePercent: number | null;
  /** null when no completed month has any spend. Do not render a forecast from null. */
  averageBurn: number | null;
  monthsObserved: number;
  remainingMonths: number;
  available: number;
  creditsStillToCome: number;
  projectedSpend: number | null;
  projectedUnused: number | null;
  contractEnd: string;
  affordable: Array<{ id: string; name: string; category: string; credits: number; quantity: number }>;
  spendByMonth: Array<{ month: string; credits: number }>;
};

/** What it takes to raise the same request again. No due date, no billing month and
 *  no credits — see reorderValuesFor on the server for why. */
export type ReorderValues = {
  serviceId: string;
  title: string;
  brief: string;
  quantity: number;
  language: string;
  channel: string | null;
  referenceUrl: string | null;
};

/** A saved brief. Belongs to the account, not the person who wrote it. */
export type BriefTemplate = {
  id: string;
  name: string;
  serviceId: string;
  serviceName: string;
  /** False when the service has been retired — do not offer it as a starting point. */
  serviceActive: boolean;
  title: string;
  brief: string;
  quantity: number;
  language: string;
  channel: string | null;
  referenceUrl: string | null;
  createdByName: string | null;
  createdAt: string;
};

export type SearchResults = {
  requests: Array<{
    id: string; title: string; brief: string; status: string;
    service_id: string; service_name: string;
    billing_month: string; created_at: string; credits: number;
  }>;
  entries: Array<{
    id: string; title: string; kind: string; credits: number;
    month: string; occurred_at: string;
    service_id: string | null; service_name: string | null; note: string | null;
  }>;
  /** A group cut at the server limit. Say so — a truncated list that does not
   *  mention it reads as the complete answer. */
  truncated: { requests: boolean; entries: boolean };
};

/* ------------------------------------------- the record of the relationship */

export type ContactSide = "client" | "prismic";

export type Contact = {
  id: string;
  side: ContactSide;
  name: string;
  title: string;
  email: string;
  phone: string;
  /** 1 when this is whose sign-off actually unblocks work. */
  is_approver: number;
  note: string;
  created_at: string;
  updated_at: string;
};

export type Meeting = {
  id: string;
  /** The day it HAPPENED, not the day it was typed up. */
  held_on: string;
  title: string;
  attendees: string;
  summary: string;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
};

export type Decision = {
  id: string;
  decided_on: string;
  title: string;
  rationale: string;
  request_id: string | null;
  meeting_id: string | null;
  /** Joined from the request. A decision stores no figure of its own — a second
   *  copy of the money is a second thing to disagree with the ledger. */
  request_title: string | null;
  service_id: string | null;
  service_name: string | null;
  request_credits: number | null;
  request_status: string | null;
  meeting_title: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
};

export type Renewal = {
  contractStart: string;
  contractEnd: string;
  monthsElapsed: number;
  monthsRemaining: number;
  daysRemaining: number;
  progress: number;
  /** Ninety days before the end. A renewal that opens on the expiry date is lost. */
  conversationBy: string;
  daysUntilConversation: number;
  status: "running" | "conversation_due" | "expiring" | "expired";
};

export type RelationshipData = {
  renewal: Renewal;
  contacts: Contact[];
  meetings: Meeting[];
  decisions: Decision[];
  documents: Attachment[];
};

export type ReviewPack = {
  quarter: string;
  months: string[];
  /** Months in the quarter the contract does not cover. Stated, never zero-filled. */
  monthsOutsideContract: string[];
  totals: {
    /** Rolled INTO the quarter. Its own figure, never folded into `granted` — a
     *  rollover is last month's leftover appearing again, not new money. */
    carriedIn: number;
    /** New credits only: allocations, top-ups, refunds, positive adjustments. */
    granted: number;
    spent: number;
    committed: number;
    /** The ledger's own closing figure. This is what the screen prints. */
    closing: number;
    /** Rebuilt from the parts, shown only to be checked against `closing`. */
    closingFromParts: number;
    reconciled: boolean;
    drift: number;
  };
  byMonth: Array<{ month: string; granted: number; spent: number; available: number }>;
  delivered: Array<{
    id: string; title: string; service_id: string; service_name: string;
    service_category: string; credits: number; completed_at: string | null;
    billing_month: string; cover_url: string | null;
  }>;
  byCategory: Array<{ category: string; credits: number }>;
  meetings: Meeting[];
  decisions: Decision[];
  stillOpen: Array<{ id: string; title: string; status: string; credits: number; billing_month: string }>;
};

export type ReviewData = { pack: ReviewPack; quarters: string[]; renewal: Renewal };

/* ----------------------------------------------------- closing a month */

export type BlockingRequest = { id: string; title: string; status: string; credits: number };

/** What a manager is asked to approve, and what they approved. */
export type MonthClose = {
  month: string;
  closed: boolean;
  closedAt: string | null;
  closedBy: string | null;
  note: string;
  /** The figure that WILL carry (open) or DID carry (closed). */
  rollover: number;
  balance: { totalPool: number; spent: number; reserved: number; available: number };
  /** Work billed to this month that has not been delivered. Closing MOVES it. */
  moving: BlockingRequest[];
  /** The month that work would be rebilled to. Null when there is none. */
  movingTo: string | null;
  canClose: boolean;
  blockedReason: "previous_month_open" | "not_in_contract" | "nowhere_to_move" | "next_month_closed" | null;
  /** Reopening is refused while a later month is closed; this names the first one. */
  reopenBlockedBy: string | null;
};

/** A read-only link the client can send to their own stakeholders (item 10). */
export type ShareLink = {
  token: string;
  request_id: string;
  request_title: string;
  created_by_name: string | null;
  created_at: string;
  expires_at: string;
  revoked_at: string | null;
  view_count: number;
  last_viewed_at: string | null;
  /** Derived server-side on every read — never stored, so it cannot go stale. */
  active: boolean;
};

/** Your month in review (item 9). Same pack as the quarter, one month wide. */
export type MonthReviewData = {
  pack: ReviewPack;
  /** False while the month is still running — every figure below is provisional. */
  complete: boolean;
  closed: boolean;
};

/** An agent key as the Admin page sees it. The key itself is never returned after creation. */
export type AgentKey = {
  id: string;
  name: string;
  prefix: string;
  platform: boolean;
  actsAs: { email: string; name: string | null } | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
};
